/**
 * Two-pass swiper initializer.
 *
 * Pass 1 (per section, synchronous):
 *   - Skip if `data-mobile-breakpoint` is set and the viewport is wider than
 *     it (mobile-only sections destroy on desktop, init on mobile).
 *   - If a plugin's `gateInit(section)` claims the section, hand it to that
 *     plugin's `runGate(section, ctx, helpers)` and skip the normal pipeline.
 *     Gate plugins are responsible for the entire section lifecycle from
 *     there, including lazy-creating swipers via `helpers.initSwiper`.
 *   - Otherwise, for each .swiper inside (parents first, then nested):
 *       parseOptions  →  new Swiper(...)  →  afterInit
 *
 * Pass 2 (after every non-gated section is constructed):
 *   - For every swiper, run `afterAllInit`. This is the only place plugins
 *     can rely on the full swiper graph being present (sync groups, group
 *     autoplay, control buttons spanning multiple swipers, etc.).
 *
 * No setTimeouts. The historical 150ms delay before nested-swiper init was a
 * symptom, not a fix — it existed because pagination element lookup was
 * scattered across init phases. Now that nested pagination resolution happens
 * in `parseOptions` and parents always init before nesteds in the same
 * synchronous tick, the delay is unnecessary.
 */

import Swiper from 'swiper/bundle';

import defaultRegistry from './registry.js';
import {
  sortPlugins,
  applicablePlugins,
  runHook,
  runParseOptions,
  findGate,
} from './lifecycle.js';

import { defaultOptions } from '../config/default-options.js';
import { parseOptionsFromAttributes } from '../config/options-parser.js';
import { isMobile } from '../utils/breakpoint-detector.js';
import { debounce } from '../utils/debounce.js';

const SECTION_SELECTOR = '.swiper-section';

export class Initializer {
  /**
   * @param {Object} opts
   * @param {Array} opts.plugins              Plugin objects (validated and sorted on construction).
   * @param {Document|HTMLElement} [opts.root=document]  Root to scan for .swiper-section.
   * @param {Object} [opts.registry]          Registry instance. Defaults to the
   *                                          module-level singleton, which is
   *                                          what production uses (and what
   *                                          `installBackCompat` proxies). Pass
   *                                          a fresh `new SwiperRegistry()` for
   *                                          tests or for an isolated sub-tree
   *                                          initializer; never share registries
   *                                          across two long-lived initializers
   *                                          on the same page (the registry
   *                                          owns destroy semantics).
   */
  constructor({ plugins = [], root = document, registry = defaultRegistry } = {}) {
    this.plugins = sortPlugins(plugins);
    this.root = root;
    this.registry = registry;
    // Debounce the resize handler. `window.resize` fires at ~60Hz during a
    // drag-resize; running `_initOrDestroyAll` (full DOM scan) on every fire
    // is wasteful. 100ms is below the user-perceptible threshold for "the
    // carousel reflowed" but well above one frame, collapsing a drag-burst
    // into a single trailing call once the user pauses.
    this.handleResize = debounce(this._handleResizeNow.bind(this), 100);
    this._resizeBound = false;
    // Shared resize bus. Plugins that need their own resize-driven logic
    // (e.g. autoplay's viewport-cross rebuild) subscribe via `onResize()`
    // instead of attaching their own `window.addEventListener('resize',
    // …)`, which used to mean N+1 listeners and N+1 debounce timers per
    // page. Subscribers fire AFTER the initializer's own scan completes
    // so they see a stable post-resize registry.
    this._resizeSubscribers = new Set();
  }

  /**
   * Subscribe to the shared resize bus. The handler is called inside the
   * debounced resize tick, after `_initOrDestroyAll` has finished its own
   * reconciliation pass — so the registry reflects the post-resize state by
   * the time subscribers run.
   *
   * Plugins should prefer this over attaching their own `window.resize`
   * listeners. Multiple plugins subscribing means one shared debounce
   * timer instead of N parallel ones racing each other.
   *
   * Returns an unsubscribe function. Plugins should call it from their
   * `onDestroy` hook to avoid the handler being driven against a destroyed
   * swiper / detached section.
   *
   * @param {() => void} handler
   * @returns {() => void} unsubscribe
   */
  onResize(handler) {
    if (typeof handler !== 'function') return () => {};
    this._resizeSubscribers.add(handler);
    return () => {
      this._resizeSubscribers.delete(handler);
    };
  }

  /**
   * Run init pass and start listening for resize-driven re-init.
   */
  init() {
    this._initOrDestroyAll();
    if (!this._resizeBound) {
      window.addEventListener('resize', this.handleResize);
      this._resizeBound = true;
    }
  }

  /**
   * Stop responding to resize and tear down every section we manage.
   * Useful when a new Initializer takes over or for tests.
   */
  destroy() {
    if (this._resizeBound) {
      window.removeEventListener('resize', this.handleResize);
      this.handleResize.cancel?.();
      this._resizeBound = false;
    }
    // Drop resize subscribers BEFORE destroying sections. Plugin onDestroy
    // hooks call their own unsubscribe (returned from onResize), but we
    // also clear here so a stale subscriber from a malformed plugin can't
    // outlive the initializer.
    this._resizeSubscribers.clear();
    const sections = [];
    this.registry.forEachSection((_, section) => sections.push(section));
    sections.forEach((section) => this._destroySection(section));
  }

  _handleResizeNow() {
    this._initOrDestroyAll();
    // Fan out to subscribed plugins. Order is insertion order (Set
    // guarantees that). Each subscriber is isolated: a thrown error
    // doesn't stop the rest of the chain.
    this._resizeSubscribers.forEach((handler) => {
      try {
        handler();
      } catch (err) {
        console.error('Initializer resize subscriber failed:', err);
      }
    });
  }

  /**
   * Reconcile the registry with the current DOM and run an init pass.
   *
   * Use case: Shopify's Section Rendering API (Theme Editor live edits, dev
   * server section hot-reloads, AJAX-driven section refreshes) replaces a
   * section's HTML in place. The section element we registered against is now
   * detached from the document; its swipers are bound to dead DOM. We sweep
   * those stale entries, then run the standard init pass so the freshly
   * inserted section element gets picked up.
   *
   * Cheap to call on every `shopify:section:load`: it only touches sections
   * already in the registry plus any newly-eligible ones.
   *
   * No-ops gracefully if no entries are stale and no new sections exist.
   */
  refresh() {
    const detached = [];
    this.registry.forEachSection((_swipers, section) => {
      if (!document.contains(section)) {
        detached.push(section);
      }
    });
    detached.forEach((section) => this.registry.deleteSection(section));

    this._initOrDestroyAll();
  }

  /**
   * Tear down every swiper in `section` and re-run the init pass so that
   * section gets a fresh build.
   *
   * Use case: viewport-dependent layout that Swiper's own breakpoint handling
   * can't fully reset. The carousel state (activeIndex, wrapper translate,
   * any sticky params not explicitly overridden in the new breakpoint) lingers
   * across a viewport crossing, leaving the section visually broken. A clean
   * destroy + rebuild matches the initial-load layout exactly.
   *
   * Currently used by the `autoplay` plugin's custom-autoplay path on a
   * debounced window resize when `allSlidesFit(swiper)` flips. Other plugins
   * may call this if they detect they need a fresh section build.
   *
   * Caller responsibilities:
   *   - Debounce. The init pass walks the DOM and is cheap but not free; firing
   *     it on every `resize` event during a drag-resize would be wasteful.
   *   - Don't call from inside an `onDestroy` hook for a swiper in this section
   *     — that's already mid-teardown and will get rebuilt in the same flow.
   */
  rebuildSection(section) {
    if (this.registry.hasSection(section)) {
      this._destroySection(section);
    }
    this._initOrDestroyAll();
  }

  // ---------------- internals ----------------

  _initOrDestroyAll() {
    const sections = this.root.querySelectorAll(SECTION_SELECTOR);
    /** @type {{ swiper: any, section: HTMLElement }[]} */
    const allConstructed = [];

    sections.forEach((section) => {
      const swiperEls = section.querySelectorAll('.swiper');
      if (!swiperEls.length) return;

      // Viewport gate: destroy if we shouldn't be active here.
      if (!isMobile(section)) {
        if (this.registry.hasSection(section)) {
          this._destroySection(section);
        }
        return;
      }

      if (this.registry.hasSection(section)) {
        return;
      }

      // Plugin gate (e.g. tab-system takes over for tabbed sections).
      const gate = findGate(this.plugins, section);
      if (gate) {
        const sectionSwipers = [];
        this.registry.setSectionSwipers(section, sectionSwipers);

        const ctx = this._buildContext(section, section, sectionSwipers);
        const helpers = this._buildGateHelpers(section, sectionSwipers);

        if (typeof gate.runGate !== 'function') {
          console.error(
            `Swiper plugin "${gate.name}" claims sections via gateInit ` +
              `but does not implement runGate. The section will not init.`
          );
          return;
        }
        try {
          gate.runGate(section, ctx, helpers);
        } catch (err) {
          console.error(
            `Swiper plugin "${gate.name}" runGate failed:`,
            err
          );
        }
        return;
      }

      const built = this._initSection(section, swiperEls);
      this.registry.setSectionSwipers(section, built);

      built.forEach((swiper) => allConstructed.push({ swiper, section }));
    });

    // Pass 2: afterAllInit fires once every section is constructed.
    allConstructed.forEach(({ swiper, section }) => {
      const parentSwiperSection =
        swiper.el.closest('.swiper-section') || section;
      const sectionSwipers =
        this.registry.getSectionSwipers(section) || [];
      const ctx = this._buildContext(section, parentSwiperSection, sectionSwipers);
      const apps = applicablePlugins(this.plugins, swiper.el, parentSwiperSection);
      runHook(apps, 'afterAllInit', swiper, ctx);
    });
  }

  _initSection(section, swiperEls) {
    // Parents synchronously first, then nested. This replaces the old 150ms
    // setTimeout: by the time a nested swiper looks up its parent in
    // parseOptions, the parent already exists in the same tick.
    const els = Array.from(swiperEls);
    const parents = els.filter((el) => !el.classList.contains('nested-swiper'));
    const nesteds = els.filter((el) =>  el.classList.contains('nested-swiper'));
    const ordered = [...parents, ...nesteds];

    const sectionSwipers = [];

    for (const el of ordered) {
      const parentSwiperSection = el.closest('.swiper-section') || section;

      // Section ownership: when a section contains nested .swiper-section
      // descendants (e.g. v3-plans-with-cards has one nested section per card),
      // querySelectorAll('.swiper') above is recursive and surfaces every
      // descendant .swiper. We only initialize swipers whose closest
      // .swiper-section is this iteration's section — the nested sections
      // will own their own swipers when they're iterated next.
      if (parentSwiperSection !== section) continue;

      // Per-swiper try/catch isolates failures: a malformed swiper (bad
      // markup, missing pagination element, plugin throwing inside
      // afterInit, Swiper constructor exception) used to abort the entire
      // page's init pass — every section after the failing one in
      // querySelectorAll order would silently skip. Now each swiper is its
      // own failure domain. We log enough context (section + element +
      // section.id when present) to track down what broke without
      // re-running locally with the same content.
      try {
        this._buildSwiper(el, section, parentSwiperSection, sectionSwipers);
      } catch (err) {
        console.error(
          `Swiper init failed for ${describeSwiperFailure(section, el)}; ` +
            `skipping this swiper, continuing with the rest of the page.`,
          { section, swiperEl: el, error: err }
        );
      }
    }

    return sectionSwipers;
  }

  /**
   * Build one swiper through the full plugin pipeline. Used by both the
   * normal per-section pipeline (`_initSection`) and gate plugins that
   * lazy-create swipers via `helpers.initSwiper` (e.g. tab-system).
   *
   * Side effects:
   *   - Pushes the new swiper into `sectionSwipers`.
   *   - Registers `section.id → swiper` if the section has an id.
   *   - Wires the destroy hook that runs `onDestroy` plugin hooks.
   *   - Runs `afterInit` plugin hooks.
   *
   * Returns the constructed swiper (after `new Swiper(el, options)`, so
   * already initialized).
   */
  _buildSwiper(el, section, parentSwiperSection, sectionSwipers) {
    const ctx = this._buildContext(section, parentSwiperSection, sectionSwipers);
    const apps = applicablePlugins(this.plugins, el, parentSwiperSection);

    // Build options: defaults → data-attribute parser → plugin parseOptions.
    let options = parseOptionsFromAttributes(parentSwiperSection, defaultOptions);
    options = runParseOptions(apps, el, parentSwiperSection, options, ctx);

    const swiper = new Swiper(el, options);
    sectionSwipers.push(swiper);

    // Back-compat: external callers expect window.sectionSwiperRegistry
    // to be keyed by section element id.
    if (section.id) {
      this.registry.registerById(section.id, swiper);
    }

    // Centralized destroy hook: re-evaluate plugin applicability at destroy
    // time and let each clean up its own state.
    swiper.on('destroy', () => {
      const destroyApps = applicablePlugins(
        this.plugins,
        el,
        parentSwiperSection
      );
      const destroyCtx = this._buildContext(
        section,
        parentSwiperSection,
        sectionSwipers
      );
      runHook(destroyApps, 'onDestroy', swiper, destroyCtx);

      if (section.id && this.registry.getById(section.id) === swiper) {
        this.registry.unregisterById(section.id);
      }
    });

    runHook(apps, 'afterInit', swiper, ctx);
    return swiper;
  }

  /**
   * Build the helpers object passed to a gate plugin's `runGate`. Wraps
   * `_buildSwiper` plus an immediate `afterAllInit` invocation so lazily
   * created swipers participate in the full plugin lifecycle (sync groups,
   * autoplay controls, etc.) without the gate plugin having to know
   * pipeline internals.
   *
   * Note for gate plugin authors: when you lazy-create swipers (e.g. one per
   * tab on first activation), there's no "all swipers init'd" moment — some
   * tabs may never be visited. `afterAllInit` therefore fires per-swiper as
   * each one is created. Plugins that coordinate multiple swipers (like
   * synced-swipers) cope by reading the registry on each event, so a partial
   * group is fine.
   */
  _buildGateHelpers(section, sectionSwipers) {
    return {
      Swiper,
      plugins: this.plugins,
      initSwiper: (el, parentSwiperSection) => {
        const swiper = this._buildSwiper(
          el,
          section,
          parentSwiperSection,
          sectionSwipers
        );
        const ctx = this._buildContext(section, parentSwiperSection, sectionSwipers);
        const apps = applicablePlugins(this.plugins, el, parentSwiperSection);
        runHook(apps, 'afterAllInit', swiper, ctx);
        return swiper;
      },
    };
  }

  _destroySection(section) {
    // Registry.deleteSection already calls destroy() on each swiper, which
    // fires the 'destroy' listener registered above and runs onDestroy hooks.
    this.registry.deleteSection(section);
  }

  _buildContext(section, parentSwiperSection, sectionSwipers) {
    return {
      section,
      parentSwiperSection,
      registry: this.registry,
      sectionSwipers,
      // Exposed so plugins can request a section-level rebuild (e.g. the
      // autoplay plugin uses `initializer.rebuildSection(section)` on a
      // debounced resize when crossing the viewport boundary that flips
      // `allSlidesFit`, restoring the initial-load layout). Plugins should
      // NOT poke at private members; treat this as the public surface only.
      initializer: this,
    };
  }
}

/**
 * Build a short, search-friendly identifier for a section/element pair when
 * logging swiper-init failures. Prefers `section.id` (most useful for grepping
 * server logs), falls back to a positional description.
 */
function describeSwiperFailure(section, swiperEl) {
  if (section && section.id) {
    return `section #${section.id}`;
  }
  const swiperClass = swiperEl?.className || '';
  return `<.swiper-section> with .swiper.${swiperClass.split(/\s+/)[0] || ''}`.trim();
}

export default Initializer;
